import React from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { faPlayCircle, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Sorting } from 'src/types/Sorting';
import { Switch } from 'src/components/common/Switch';
import TextWithEllipsis from 'src/components/common/TextWithEllipsis';
import { ColumnProps, PaginationConfig, SortOrder } from 'antd/lib/table';
import { AlertItem } from '../../types/AlertConfiguration';
import { OperationType } from '../../types/SelectedOperation';
import UserContext from '../../../../services/UserContex';
import styles from './AlertTable.module.less';

const DeleteIcon = faTrashAlt as IconProp;
const RunIcon = faPlayCircle as IconProp;
const RefreshIcon = faSyncAlt as IconProp;

export const defaultPagination: PaginationConfig = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};

const MAX_ROW_LENGTH = 50;

export type ColumnId = keyof AlertItem;

export type ColumnSorting = Sorting<AlertItem> & {
  field: ColumnId;
};

type StandardColumn = ColumnProps<AlertItem>;

type FilterableColumn = StandardColumn & { dataIndex?: ColumnId };

type ActionHandler = (record: AlertItem, type: OperationType) => void;

type AssociateHandler = (alert: AlertItem) => void;

const sortOrder = (sortedInfo: Sorting<AlertItem> | null, key: keyof AlertItem): SortOrder | boolean => {
  return sortedInfo && sortedInfo.field === key ? sortedInfo.order : false;
};

const renderActions = (record: AlertItem, onAction: ActionHandler) => {
  const isDisabledRunAlert = record.executionStatus.toLowerCase() === 'executing';

  return (
    <div className={styles.buttonsActions}>
      <FontAwesomeIcon
        icon={RunIcon}
        onClick={() => !isDisabledRunAlert && onAction(record, OperationType.RunAlert)}
        className={clsx(styles.icon, isDisabledRunAlert && styles.disabledIcon)}
        title={'Run'}
      />
      <FontAwesomeIcon
        onClick={() => onAction(record, OperationType.DeleteAlert)}
        icon={DeleteIcon}
        className={styles.icon}
        title={'Delete'}
      />
    </div>
  );
};

const renderDescription = (val: string | null) => {
  return <TextWithEllipsis value={val} maxLength={MAX_ROW_LENGTH} />;
};

const renderTimestamp = (timestamp: string | null) => {
  const dateFormat = UserContext.getDateFormat();
  return UserContext.reformatUTCDate(timestamp, `${dateFormat} HH:mm`);
};

const renderAlertName = (alert: AlertItem, onAction: ActionHandler) => {
  return (
    <a className={styles.alertName} onClick={() => onAction(alert, OperationType.EditAlert)}>
      {alert.alertName}
    </a>
  );
};

const renderAlertAssociation = (associated: boolean, alert: AlertItem, onAssociate: AssociateHandler) => {
  return <Switch checked={associated} key={alert.alertId} onChange={() => onAssociate(alert)} />;
};

const renderCreated = (timestamp: string | null, record: AlertItem) => {
  if (timestamp) {
    return (
      <>
        {renderTimestamp(timestamp)}
        <br />
        {record.createdBy}
      </>
    );
  }
  return record.createdBy;
};

const actionsTitle = (reloadList: () => void) => {
  return (
    <>
      Actions
      <FontAwesomeIcon
        title={'Refresh Alert Page'}
        icon={RefreshIcon}
        className={styles.refreshIcon}
        size={'lg'}
        onClick={() => reloadList()}
      />
    </>
  );
};

export const getColumns = (
  sorting: ColumnSorting,
  onAction: ActionHandler,
  associated: boolean,
  onAssociate: AssociateHandler,
  reloadList: () => void
) => {
  const columns: FilterableColumn[] = [
    {
      title: 'Alert Name',
      dataIndex: 'alertName',
      sortOrder: sortOrder(sorting, 'alertName'),
      render: (_, record) => renderAlertName(record, onAction),
      sorter: true,
    },
    {
      title: 'Alert Description',
      dataIndex: 'alertDescription',
      sortOrder: sortOrder(sorting, 'alertDescription'),
      render: renderDescription,
      sorter: true,
    },
    {
      title: 'Execution Status',
      dataIndex: 'executionStatus',
      sortOrder: sortOrder(sorting, 'executionStatus'),
      sorter: true,
    },
    {
      title: 'Last Run',
      dataIndex: 'completeTimestamp',
      sortOrder: sortOrder(sorting, 'completeTimestamp'),
      render: renderTimestamp,
      sorter: true,
    },
    {
      title: 'Alert Association',
      className: styles.xCenterColumn,
      render: (record: AlertItem) => renderAlertAssociation(associated, record, onAssociate),
    },
    // TODO: this future functionality and were asked to hide it now
    // {
    //   title: 'UI Subscription',
    //   dataIndex: 'uiSubscription',
    //   sorter: false,
    // },
    {
      title: 'Created',
      dataIndex: 'insertTimestamp',
      sortOrder: sortOrder(sorting, 'insertTimestamp'),
      render: renderCreated,
      sorter: true,
    },
    {
      title: actionsTitle(reloadList),
      render: (_, record: AlertItem) => renderActions(record, onAction),
      width: 50,
    },
  ];

  return columns;
};
